import React, { useState, useEffect } from 'react';
import { apiCall } from '../config';
import './ManageAPIConnections.css';

const ManageAPIConnections = () => {
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatuses, setConnectionStatuses] = useState({
    google: null,
    amazon: null,
    quicken: null
  });
  const [connectionDetails, setConnectionDetails] = useState({
    google: null,
    amazon: null,
    quicken: null
  });
  const [error, setError] = useState(null);

  // Check if we're returning from OAuth flow
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authResult = urlParams.get('auth');
    const page = urlParams.get('page');
    
    if (authResult === 'success' && page === 'test-google-connection') {
      // Update Google status to success
      setConnectionStatuses(prev => ({ ...prev, google: 'success' }));
      setError(null);
      // Clean up URL
      window.history.replaceState({}, document.title, '/manage-api-connections');
    } else if (authResult === 'error' && page === 'test-google-connection') {
      setConnectionStatuses(prev => ({ ...prev, google: 'error' }));
      setError('OAuth flow failed. Please try again.');
      // Clean up URL
      window.history.replaceState({}, document.title, '/manage-api-connections');
    }
    
    // Set page title
    document.title = 'Manage API Connections';
  }, []);

  const handleCheckStatus = async () => {
    setIsCheckingStatus(true);
    setError(null);

    try {
      // Check Google connection
      const response = await apiCall('/api/manage-api-connections/check-google', 'GET');
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success) {
          setConnectionStatuses(prev => ({ ...prev, google: 'success' }));
          setConnectionDetails(prev => ({ 
            ...prev, 
            google: {
              email: data.details?.email || 'Connected successfully',
              message: data.message
            }
          }));
        } else {
          setConnectionStatuses(prev => ({ ...prev, google: 'error' }));
          setConnectionDetails(prev => ({ 
            ...prev, 
            google: {
              message: data.error || 'Connection failed'
            }
          }));
        }
      } else {
        setConnectionStatuses(prev => ({ ...prev, google: 'error' }));
        setConnectionDetails(prev => ({ 
          ...prev, 
          google: {
            message: 'Connection failed'
          }
        }));
      }
    } catch (err) {
      setConnectionStatuses(prev => ({ ...prev, google: 'error' }));
      setConnectionDetails(prev => ({ 
        ...prev, 
        google: {
          message: `Connection failed: ${err.message}`
        }
      }));
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleReconnectGoogle = async () => {
    setIsConnecting(true);
    setError(null);
    setConnectionStatuses(prev => ({ ...prev, google: null }));

    try {
      const response = await apiCall('/api/manage-api-connections/oauth/initiate', 'GET');
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.auth_url) {
          // Open Google OAuth in new window/tab
          window.location.href = data.auth_url;
        } else {
          setError('Failed to get authorization URL');
          setConnectionStatuses(prev => ({ ...prev, google: 'error' }));
        }
      } else {
        setError('Failed to get authorization URL');
        setConnectionStatuses(prev => ({ ...prev, google: 'error' }));
      }
    } catch (err) {
      setError(`Failed to initiate OAuth: ${err.message}`);
      setConnectionStatuses(prev => ({ ...prev, google: 'error' }));
    } finally {
      setIsConnecting(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'success') return '✅';
    if (status === 'error') return '❌';
    return '⏳';
  };

  const getStatusText = (api, status, details) => {
    if (api === 'google') {
      if (status === 'success') {
        return details?.email || 'Connected successfully';
      } else if (status === 'error') {
        return details?.message || 'Connection failed';
      }
    }
    return 'Not connected';
  };

  return (
    <div className="manage-api-connections">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Manage API Connections</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Monitor and manage connections to external APIs</li>
              <li>Test connection status and reconnect when needed</li>
              <li>View connection details and account information</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="section">
        <div className="section-header">
          <h2>API Connection Status</h2>
        </div>
        
        <div className="button-container">
          <button 
            className="check-status-button"
            onClick={handleCheckStatus}
            disabled={isCheckingStatus}
          >
            {isCheckingStatus ? 'Checking Status...' : 'Check Status'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="api-connections-table">
          <table>
            <thead>
              <tr>
                <th>API Service</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Google Row */}
              <tr>
                <td>
                  <div className="api-service">
                    <span className="api-icon">🔗</span>
                    <span className="api-name">Google</span>
                  </div>
                </td>
                <td>
                  <div className="status-cell">
                    <span className="status-icon">
                      {getStatusIcon(connectionStatuses.google)}
                    </span>
                    <div className="status-details">
                      <span className="status-text">
                        {getStatusText('google', connectionStatuses.google, connectionDetails.google)}
                      </span>
                    </div>
                  </div>
                </td>
                <td>
                  <button 
                    className="reconnect-button"
                    onClick={handleReconnectGoogle}
                    disabled={isConnecting || connectionStatuses.google !== 'error'}
                  >
                    {isConnecting ? 'Connecting...' : 'Reconnect'}
                  </button>
                </td>
              </tr>

              {/* Amazon Row */}
              <tr className="coming-soon">
                <td>
                  <div className="api-service">
                    <span className="api-icon">📦</span>
                    <span className="api-name">Amazon</span>
                    <span className="coming-soon-badge">Coming Soon</span>
                  </div>
                </td>
                <td>
                  <div className="status-cell">
                    <span className="status-icon">⏳</span>
                    <div className="status-details">
                      <span className="status-text">Not available yet</span>
                    </div>
                  </div>
                </td>
                <td>
                  <button 
                    className="reconnect-button disabled"
                    disabled={true}
                  >
                    Coming Soon
                  </button>
                </td>
              </tr>

              {/* Quicken Row */}
              <tr className="coming-soon">
                <td>
                  <div className="api-service">
                    <span className="api-icon">💰</span>
                    <span className="api-name">Quicken</span>
                    <span className="coming-soon-badge">Coming Soon</span>
                  </div>
                </td>
                <td>
                  <div className="status-cell">
                    <span className="status-icon">⏳</span>
                    <div className="status-details">
                      <span className="status-text">Not available yet</span>
                    </div>
                  </div>
                </td>
                <td>
                  <button 
                    className="reconnect-button disabled"
                    disabled={true}
                  >
                    Coming Soon
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageAPIConnections; 